import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  booleanAttribute,
  computed,
  contentChild,
  input,
} from '@angular/core';

import { TwPageHeaderBreadcrumbsComponent } from './tw-page-header-breadcrumbs.component';

/** Size axis for {@link TwPageHeaderComponent}.
 *
 * - `default` — full vertical rhythm — used on top-level pages where the
 *   header is the dominant focal point of the page.
 * - `compact` — tighter inter-row spacing + smaller title for detail pages,
 *   drawers, or any surface where the header is supporting chrome rather
 *   than the centerpiece. Mirrors the prototype's `PHRoot compact` branch. */
export type TwPageHeaderSize = 'compact' | 'default';

/**
 * Page header primitive (Portal Design System — Page Header feature).
 *
 * Renamed successor of the retired `tw-page-hero`. The page-top identity
 * band for every portal page. Composes an **owned, elevated breadcrumb
 * row** (the lead element, with segment-switching + deep-trail-collapse
 * powers), a title row (optional leading icon + static **or** editable
 * title + adornment slot for chips/badges/live-indicator + right-side
 * primary buttons and overflow dropdown), an optional subtitle/meta strip,
 * optional attached tabs, and a `default`/`compact` density.
 *
 * ```
 * [Home › Devices › Device Profile ▾ › v3 · Draft ▾]   (owned · LEAD · elevated)
 * [icon] [H1 Title  ·badge ·LIVE]          [primary btn] [primary btn] [⋮]
 * [📍 Brussels · Active since Mar 2024 · 22 devices · ● Gateway online]
 * [──────────────── Tabs if present ────────────────]
 * ```
 *
 * ## API
 *
 * - `size` (`'default' | 'compact'`, default `'default'`) — controls the
 *   inter-row spacing + the title size (compact reduces both).
 *
 * ## Sub-components (separate selectors, all optional)
 *
 * - `<tw-page-header-breadcrumbs>` — owned, elevated **lead** crumb row.
 *   `[breadcrumbs]` items / `auto` route-walking / projection, plus the
 *   `variant` input (chevron / slash / icon / pill / segment / collapse).
 * - `<tw-page-header-title>` — optional leading `iconKey`, static **or**
 *   editable title (pencil affordance, Enter/Escape, dirty flag,
 *   `titleSave` output), a `title-adornment` projection slot, and an
 *   `actions` projection slot (inline-right by default, `twoTier` option).
 * - `<tw-page-header-subtitle>` — sep-dot inline meta strip (default).
 * - `<tw-page-header-actions>` — declarative `[actions]` overflow `⋮`
 *   dropdown PLUS a projection slot for primary buttons.
 * - `<tw-page-header-tabs>` — thin wrapper around `<tw-tabs>` with count
 *   pills + animated active underline. When present, the page-header
 *   suppresses its own bottom rule because the tabs nav paints one.
 *
 * Each sub-component is projected into a named grid area via element-name
 * `<ng-content select="...">` selectors — consumers can place the
 * sub-components in any order; the grid template areas resolve the
 * canonical layout regardless.
 *
 * `ViewEncapsulation.None` is required because the header styles its
 * named-slot wrappers via cross-boundary BEM selectors so the
 * sub-components cooperate with the parent grid without per-sub-component
 * SCSS — same pattern used by `tw-empty-state`, `tw-card`, `tw-toolbar`.
 *
 * ## Token map (prototype dark-only hex → `--twist-*`)
 *
 *   canvas `#0a0e0e`            → ambient page surface (inherited)
 *   accent mint `#6fdbaa`       → `--twist-color-accent`
 *   inter-row gap (default)     → `--twist-spacing-3`
 *   inter-row gap (compact)     → `--twist-spacing-2`
 *   bottom rule                 → 1px solid `--twist-color-border`
 *   tabs supersede rule         → `:has(tw-page-header-tabs)` suppresses
 *                                  the wrapper's own bottom border
 */
@Component({
  selector: 'tw-page-header',
  standalone: true,
  imports: [TwPageHeaderBreadcrumbsComponent],
  templateUrl: './tw-page-header.component.html',
  styleUrl: './tw-page-header.component.scss',
  host: {
    '[class]': 'classes()',
    '[attr.data-size]': 'dataSize()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class TwPageHeaderComponent {
  readonly size = input<TwPageHeaderSize>('default');

  /**
   * Value bound to the `data-size` host attribute. Coerced to a defined
   * value so the attribute never reads `undefined` during the host
   * binding's first evaluation (which would otherwise differ from the
   * applied input default on the following check).
   */
  protected readonly dataSize = computed(() => this.size() ?? 'default');

  /**
   * Whether the header renders its own breadcrumb lead band. Default `true`
   * — the header AUTO-RENDERS a `<tw-page-header-breadcrumbs auto />` (segment
   * default) so no page needs to declare the element. Set `[breadcrumb]="false"`
   * for the rare bare-wrapper route that must show no crumb at all.
   *
   * Opt-OUT, not opt-IN: a page that DOES project its own
   * `<tw-page-header-breadcrumbs ...>` (e.g. analytics' `[breadcrumbs]` items
   * mode, or any `variant` override) takes precedence — the auto one is then
   * suppressed (see {@link hasProjectedBreadcrumbs}) so there is never a
   * double crumb.
   */
  readonly breadcrumb = input<boolean, unknown>(true, { transform: booleanAttribute });

  /**
   * A consumer-projected `<tw-page-header-breadcrumbs>` (any mode/variant),
   * if present. Queried so the header can SUPPRESS its own auto breadcrumb
   * when the page supplies its own — projected wins, no double crumb.
   */
  private readonly projectedBreadcrumbs = contentChild(TwPageHeaderBreadcrumbsComponent);

  /** True when the page projects its own breadcrumb element. */
  protected readonly hasProjectedBreadcrumbs = computed(
    () => this.projectedBreadcrumbs() !== undefined,
  );

  /**
   * Render the header's own `auto` breadcrumb only when breadcrumbs are
   * enabled AND the page didn't project its own. ViewEncapsulation.None means
   * the view-rendered element is a direct child of the host, so the
   * `.tw-page-header > tw-page-header-breadcrumbs { grid-area: breadcrumbs }`
   * rule places it in the lead band exactly like a projected one.
   */
  protected readonly renderAutoBreadcrumb = computed(
    () => this.breadcrumb() && !this.hasProjectedBreadcrumbs(),
  );

  protected readonly classes = computed(() => {
    return ['tw-page-header', `tw-page-header--${this.size()}`].join(' ');
  });
}
